/**
 * P-2 日報を開く (§14-2)。画面を持たない遷移専用ルート。
 * `GET /daily-reports/form` を呼び、作成/再利用された daily_report_id を得て
 * P-3 (`/daily-reports/:id`) へ `replace` 遷移する。
 *
 * ⚠️ P-2 のクエリパラメータのまま留まると副作用のあるページがブックマークされてしまうため、
 * 必ず P-3 の URL (daily_report_id を含む) へ replace する (§14-2 の設計意図)。
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { ApiClientError } from '@/lib/api';
import { fetchDailyReportForm } from '@/lib/daily-report-api';

export function DailyReportOpenPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const requestedRef = useRef(false);

  const nodeId = params.get('node_id');
  const businessDate = params.get('business_date') ?? undefined;

  useEffect(() => {
    if (!nodeId) { setError('店舗が指定されていません。日報一覧から開き直してください。'); return; }
    if (requestedRef.current) return;
    requestedRef.current = true;
    (async () => {
      try {
        const form = await fetchDailyReportForm(nodeId, businessDate);
        navigate(`/daily-reports/${form.daily_report.id}`, { replace: true, state: { formResponse: form } });
      } catch (e) {
        setError(e instanceof ApiClientError ? e.message : '日報を開けませんでした');
      }
    })();
  }, [nodeId, businessDate, navigate]);

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-10 text-center space-y-2">
        <p className="text-body text-danger-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 text-text-sub py-16">
      <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
      <span className="text-body">日報を開いています…</span>
    </div>
  );
}
